import { useEffect } from 'react';

/**
 * useRevealOnScroll — attaches a lightweight IntersectionObserver to every
 * element carrying the `.iv2-reveal` class inside the given root (defaults
 * to document). On first intersection, adds `.iv2-reveal-visible` and stops
 * observing that element.
 *
 * Design constraints honored:
 *   - Server-safe: bails out if `window` / `document` are undefined.
 *   - Graceful degradation: bails silently if `IntersectionObserver` is
 *     unavailable — leaves elements visible (no hidden content risk).
 *   - Reduced motion: bails when the user prefers-reduced-motion — leaves
 *     elements visible with no transition.
 *   - Cleanup: disconnects the observer on unmount.
 *
 * IMPORTANT: The `.iv2-reveal` class MUST render visible by default in CSS.
 * Only after this hook mounts do we add `.iv2-reveal-pending` to opt into
 * the hidden-then-fade behavior. That way JS-off / SSR / unsupported browsers
 * always see content.
 */
const useRevealOnScroll = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    // Respect user preference
    const mediaQuery = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : { matches: false };
    if (mediaQuery.matches) return;

    // Graceful fallback: no IntersectionObserver → leave content visible
    if (typeof window.IntersectionObserver === 'undefined') return;

    const targets = Array.from(document.querySelectorAll('.iv2-reveal'));
    if (targets.length === 0) return;

    // Now opt into the hidden state — since JS is running, we can animate
    targets.forEach((el) => el.classList.add('iv2-reveal-pending'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('iv2-reveal-pending');
            entry.target.classList.add('iv2-reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );

    targets.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      // Ensure nothing is left hidden if the component unmounts before reveal
      targets.forEach((el) => {
        el.classList.remove('iv2-reveal-pending');
      });
    };
  }, []);
};

export default useRevealOnScroll;
